'use client';

import { CmsOffer } from '@hyphengroup/cms-components/types/cmsProduct';
import { track } from 'utils/tracking';
import { APPLY_V3_STEPS } from './stepTracking';
import { ApplyV3LoginMethod, createApplyNowUserData } from './tracking';
import {
  createBaseTracking,
  createItemTracking,
  TrackProduct,
} from './tracking.shared';
import { getBundleOfferTrackingId } from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/helpers';

export type BundleUpsellContext = {
  email: string;
  loginMethod: ApplyV3LoginMethod;
  marketingConsent: boolean;
  leadId?: string;
};

function createBundleTracking(offer: CmsOffer) {
  return {
    bundle_name: offer.offer_title,
    bundle_id: getBundleOfferTrackingId(offer),
  };
}

function createContextTracking(context: BundleUpsellContext) {
  const userData = createApplyNowUserData(context.email);
  const { email_hash_md5, email_hash_sha256 } = userData;

  return {
    login_method: context.loginMethod,
    marketing_consent: context.marketingConsent ? 'yes' : 'no',
    lead_id: context.leadId ?? '',
    user_data: {
      email_hash_md5,
      email_hash_sha256,
    },
  };
}

export function trackBundleImpression(offer: CmsOffer, index: number): void {
  track({
    event: 'element_impression',
    component_type: 'card',
    component_title: 'Bundle Card_View',
    index: String(index),
    ...createBundleTracking(offer),
  });
}

export function trackBundleSelection(
  product: TrackProduct,
  offer: CmsOffer,
  index: number,
  context: BundleUpsellContext,
): void {
  track({
    event: 'reward_gateway',
    intent: 'Select Bundle',
    component_type: 'card',
    component_title: 'Bundle Card_Card',
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.bundleUpsell.number,
      APPLY_V3_STEPS.bundleUpsell.name,
    ),
    ...createContextTracking(context),
    ...createItemTracking(product, index),
    ...createBundleTracking(offer),
  });
}

export function trackBundleDetails(
  product: TrackProduct,
  offer: CmsOffer,
  index: number,
): void {
  track({
    event: 'product_interaction',
    action: 'click',
    component_type: 'card',
    component_title: 'Bundle Card_See Full Details',
    item_list_name: 'bundle upsell',
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.bundleUpsell.number,
      APPLY_V3_STEPS.bundleUpsell.name,
    ),
    ...createItemTracking(product, index),
    ...createBundleTracking(offer),
  });
}

export function trackApplyWithBundle(
  product: TrackProduct,
  offer: CmsOffer,
  index: number,
  context: BundleUpsellContext,
  ctaLabel: string,
): void {
  track({
    event: 'reward_gateway',
    intent: 'Continue With Bundle',
    component_type: 'bottom-sticky',
    component_title: `CTA_${ctaLabel}`,
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.bundleUpsell.number,
      APPLY_V3_STEPS.bundleUpsell.name,
    ),
    ...createContextTracking(context),
    ...createItemTracking(product, index),
    ...createBundleTracking(offer),
  });
}

export function trackApplyWithoutBundle(
  product: TrackProduct,
  context: BundleUpsellContext,
  ctaLabel: string,
): void {
  track({
    event: 'reward_gateway',
    intent: 'Continue Without Bundle',
    component_type: 'bottom-sticky',
    component_title: `Button_${ctaLabel}`,
    ...createBaseTracking(
      product,
      APPLY_V3_STEPS.bundleUpsell.number,
      APPLY_V3_STEPS.bundleUpsell.name,
    ),
    ...createContextTracking(context),
    ...createItemTracking(product, 0),
  });
}
